import { faCalendarCheck, faLink, faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { classnames, LoadingIndicator, Tooltip, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Badge, CreateTableComponent, SortInfo } from '@ygtechnologies/rubus-ui';
import { DateDisplay } from '@/components/localization';
import { orderBy } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getResidentialUnitsByProjectIdWithExtraData } from '@/services/residential-units';
import { IState } from '@/state/IState';
import { ResidentialUnitExtraData } from '@/types';
import { useAsyncState } from '@/utils/hooks';

import {
    adminAcceptMeeting,
    exportMeetings,
    getAllInternalMeetings,
    MeetingBooking,
    MeetingConfirmation,
    MeetingType,
} from '@/services/booking';
import { numberToClock } from '@/utils/date';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { LetterSetupData } from '../../MeetingOverview';
import { MeetingFilterOptions } from '../../MeetingsFilter';
import { DateChangeDialog } from '../DialogModals/DateChangeDialog';
import { ReminderDialog } from '../DialogModals/ReminderDialog';
import theme from './MeetingApartments.module.scss';
import { MeetingBottomBar } from './MeetingBottomBar';

interface MeetingApartmentsProps {
    activeMeetingType: MeetingType;
    letterSetup?: LetterSetupData;
    filter: MeetingFilterOptions;
    onSaved(): void;
}

export interface ResidentialUnitMeetingData extends ResidentialUnitExtraData {
    meetingType?: MeetingType;
    meetingId?: number;
    sentDate?: Date;
    confirmationDate?: Date;
    confirmedBy?: MeetingConfirmation[] | undefined;
    reminderSentDate?: Date | string;
    meetingDate?: Date | string;
    timeSlotStart?: number;
    timeSlotEnd?: number;
    isChecked: boolean;
}

type SortField = keyof ResidentialUnitMeetingData;
type Sorting = {
    sortBy: SortField;
    direction: 'asc' | 'desc';
};

const Table = CreateTableComponent<ResidentialUnitMeetingData>();

/**
 * Helper function to filter items based on date presence and filter option
 * @param dateValue - The date value to check (can be Date, string, null, or undefined)
 * @param filterOption - The filter option ('all', 'yes', 'no')
 * @returns true if the item should be included in the filtered results
 */
const filterByDatePresence = (dateValue: Date | string | null | undefined, filterOption: 'all' | 'yes' | 'no'): boolean => {
    if (filterOption === 'yes') {
        return !!dateValue;
    }
    if (filterOption === 'no') {
        return dateValue == null || dateValue === undefined;
    }
    return true; // 'all' case
};

const MeetingApartments: React.FC<MeetingApartmentsProps> = ({ activeMeetingType, letterSetup, filter, onSaved }) => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
    const [showChangeModal, setShowChangeModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [listWExtra, setListWExtra] = useState<ResidentialUnitMeetingData[]>();
    const [sortBy, setSortBy] = useState<Sorting>({ sortBy: 'name', direction: 'asc' });

    const orderedResidentialUnits = useMemo(() => {
        return orderBy(listWExtra, [sortBy.sortBy], [sortBy.direction]);
    }, [listWExtra, sortBy]);

    const selectedRowIds = useMemo(
        () => (listWExtra ?? []).filter((x) => x.isChecked).map((x) => String(x.id)),
        [listWExtra]
    );

    const [allMeetings, , reloadAllMeetings] = useAsyncState<MeetingBooking[] | null>(
        () => getAllInternalMeetings(projectId),
        [projectId],
        null
    );

    const [residentialUnits] = useAsyncState<ResidentialUnitExtraData[] | null>(
        () =>
            getResidentialUnitsByProjectIdWithExtraData(projectId, {
                floors: filter.floors,
                layoutTypes: filter.layoutTypes,
                groupIds: filter.groups,
                searchText: filter.searchText,
            }),
        [filter, projectId],
        null
    );

    useEffect(() => {
        if (residentialUnits && allMeetings) {
            setIsLoading(false);
            const listWMeetings = residentialUnits.map((residentialUnit: ResidentialUnitExtraData) => {
                const theMeeting = allMeetings.find(
                    (meeting) =>
                        meeting.residentialUnitId === residentialUnit.id && meeting.meetingType?.id === activeMeetingType.id
                );
                let unitWithMeeting: ResidentialUnitMeetingData = {
                    isChecked: listWExtra?.find((unit) => unit.id === residentialUnit.id)?.isChecked || false,
                    ...residentialUnit,
                };
                if (theMeeting !== null && theMeeting !== undefined) {
                    unitWithMeeting = {
                        ...unitWithMeeting,
                        meetingId: theMeeting.id,
                        meetingType: theMeeting.meetingType,
                        sentDate: theMeeting.sentDate,
                        meetingDate: theMeeting.meetingDate,
                        reminderSentDate: theMeeting.reminderSentDate,
                        timeSlotStart: theMeeting.timeSlotStart,
                        timeSlotEnd: theMeeting.timeSlotEnd,
                        confirmationDate: theMeeting.meetingConfirmations?.find((x) => x.confirmationDate != null)
                            ?.confirmationDate,
                        confirmedBy: theMeeting.meetingConfirmations?.filter((x) => x.confirmationDate != null),
                    };
                }
                return unitWithMeeting;
            });
            const filteredList = listWMeetings
                .filter((x) => filterByDatePresence(x.sentDate, filter.hasSent))
                .filter((x) => filterByDatePresence(x.meetingDate, filter.hasMeetingDate))
                .filter((x) => filterByDatePresence(x.confirmationDate, filter.hasConfirmed))
                .filter((x) => filterByDatePresence(x.reminderSentDate, filter.hasReminded));
            setListWExtra(filteredList);
        }
    }, [residentialUnits, allMeetings, activeMeetingType, filter]);

    const setChecked = (row: ResidentialUnitMeetingData, checked: boolean) => {
        setListWExtra((prev) =>
            prev?.map((x) => (x.id === row.id ? { ...x, isChecked: checked } : x))
        );
    };

    const handleSelectionChange = (newSelectedIds: string[]) => {
        const idSet = new Set(newSelectedIds);
        setListWExtra((prev) =>
            prev?.map((x) => ({ ...x, isChecked: idSet.has(String(x.id)) }))
        );
    };

    const handleSortChange = (sortInfo: SortInfo<ResidentialUnitMeetingData>[]) => {
        if (sortInfo.length > 0) {
            setSortBy({
                sortBy: sortInfo[0].field,
                direction: sortInfo[0].direction,
            });
        }
    };

    const checkIfAdminAccept = (row: ResidentialUnitMeetingData) => {
        if (row.confirmedBy) {
            return row.confirmedBy.filter((x) => x.confirmedByAdmin).length > 0;
        }
        return false;
    };

    const checkIfConnectedMeetings = (row: ResidentialUnitMeetingData) => {
        if (letterSetup && letterSetup?.connectedMeetings && letterSetup.connectedMeetings.length > 0) {
            const meetingsWithoutDates = letterSetup.connectedMeetings.filter((x) => {
                const connectedMeetings = allMeetings?.filter(
                    (meeting) =>
                        meeting.residentialUnitId === row.id &&
                        meeting.meetingType?.id === x &&
                        meeting.meetingDate !== undefined
                );
                if (connectedMeetings && connectedMeetings.length > 0) {
                    return false;
                }
                return true;
            });
            return meetingsWithoutDates.length > 0;
        } else {
            return false;
        }
    };

    const handleShowReminderModal = (show: boolean) => {
        setShowReminderModal(show);
    };

    const handleExportMeetings = async () => {
        let hasErrors = false;
        if (listWExtra !== undefined) {
            const checkedMeetings: number[] = listWExtra
                .filter((x) => x.isChecked)
                .map((x) => {
                    if (x.meetingId !== undefined) {
                        return x.meetingId;
                    } else {
                        Toast.error(x.name + ':\n' + t`Meeting is missing date and time.`);
                        hasErrors = true;
                        return 0;
                    }
                });
            if (!hasErrors && checkedMeetings.length > 0) {
                await exportMeetings(checkedMeetings);

                Toast.success(t`Exported.`);
            }
        }
    };

    const handleAdminAccept = async () => {
        const numChecked = orderedResidentialUnits.filter((x) => x.isChecked);
        const meetingIds = numChecked.filter((x) => x.meetingId).map((x) => x.meetingId!);
        if (meetingIds && numChecked && meetingIds.length === numChecked.length) {
            await adminAcceptMeeting(meetingIds)
                .then(() => Toast.success(<Trans>Meeting confirmed</Trans>))
                .catch(() => Toast.error(<Trans>Something went wrong.</Trans>))
                .finally(() => reloadAllMeetings());
        } else {
            Toast.error(
                t`Meetings has no date:` +
                    '\n' +
                    numChecked
                        .filter((x) => !x.meetingId)
                        .map((x) => {
                            return x.name! + ' - ' + x.residentialUnitGroupName!;
                        })
                        .join('\n'),
                { className: theme.newLines }
            );
        }
    };

    const handleSendReminder = () => {
        reloadAllMeetings();
        setShowReminderModal(false);
    };

    const handleUpdateLetterSetups = () => {
        triggerHandler(onSaved);
    };

    const handleShowChangeModal = (show: boolean) => {
        if (!show) {
            handleSelectionChange([]);
        }
        setShowChangeModal(show);
    };

    const handleConfirmChange = () => {
        reloadAllMeetings();
        setShowChangeModal(false);
    };

    const handleEditRow = (row: ResidentialUnitMeetingData) => {
        setChecked(row, true);
        handleShowChangeModal(true);
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <div>
            <ReminderDialog
                show={showReminderModal}
                selectedMeetings={listWExtra!}
                letterSetupData={letterSetup!}
                activeTab={activeMeetingType}
                onSend={() => handleSendReminder()}
                onUpdate={() => handleUpdateLetterSetups()}
                onFilterChange={setChecked}
                onClose={() => handleShowReminderModal(false)}
                onCancel={() => handleShowReminderModal(false)}
            />
            <DateChangeDialog
                show={showChangeModal}
                data={listWExtra!}
                activeTab={activeMeetingType}
                onSend={handleConfirmChange}
                onFilterChange={setChecked}
                onClose={() => handleShowChangeModal(false)}
                onCancel={() => handleShowChangeModal(false)}
            />
            <MeetingBottomBar
                listWExtra={listWExtra}
                activeMeetingTypeId={activeMeetingType.id}
                onClose={() => handleSelectionChange([])}
                showReminderModal={() => handleShowReminderModal(true)}
                showChangeModal={() => handleShowChangeModal(true)}
                adminAccept={() => handleAdminAccept()}
                exportMeetings={() => handleExportMeetings()}
            />
            
            <Table
                className={theme.meetingTable}
                data={orderedResidentialUnits}
                enableSelection
                selectedRows={selectedRowIds}
                onSelectionChange={handleSelectionChange}
                getRowId={(row) => String(row.id)}
                sortingMode="custom"
                onSortChange={handleSortChange}
                preSortedBy={{ field: 'name', direction: 'asc' }}
                hasAlternateRowColors
                selectionColumnWidth={56}>
                <Table.Column
                    sortable
                    field="name"
                    header={t`Unit`}
                    renderer={({ row }) => <span>{row.name}</span>}
                />
                <Table.Column
                    sortable
                    field="residentialUnitGroupName"
                    header={t`Unit group`}
                    renderer={({ row }) => <span>{row.residentialUnitGroupName}</span>}
                />
                <Table.Column
                    sortable
                    field="floor"
                    header={t`Floor`}
                    renderer={({ row }) => <span>{row.floor}</span>}
                />
                <Table.Column
                    header={t`Owner`}
                    renderer={({ row }) => (
                        <>
                            {row.buyerInfo?.users?.map((user, index) => (
                                <div key={index}>
                                    <span>{user.displayName}</span>
                                </div>
                            ))}
                        </>
                    )}
                />
                <Table.Column
                    hidden={!activeMeetingType.needsConfirmation}
                    header={t`Status`}
                    renderer={({ row }) => (
                        <div className={theme.statusWrapper}>
                            <div>
                                {row.buyerInfo?.users?.map((user, index) => (
                                    <div key={index}>
                                        {row.sentDate &&
                                            (row.confirmedBy?.find((x) => x.applicationUserId === user.userId)
                                                ?.confirmationDate ? (
                                                <Tooltip
                                                    dynamicWidth={true}
                                                    content={
                                                        <div>
                                                            <div>
                                                                <Trans>Meeting invitations is confirmed by:</Trans>
                                                            </div>
                                                            <div>{user.displayName}</div>
                                                            <div>
                                                                <DateDisplay
                                                                    value={
                                                                        row.confirmedBy.find(
                                                                            (x) => x.applicationUserId === user.userId
                                                                        )?.confirmationDate
                                                                    }
                                                                    monthTextForm={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    }>
                                                    <FontAwesomeIcon
                                                        className={theme.confirmedIcon}
                                                        icon={faCalendarCheck}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    dynamicWidth={true}
                                                    content={
                                                        <div>
                                                            <Trans>This meeting has not been confirmed by this buyer.</Trans>
                                                        </div>
                                                    }>
                                                    <FontAwesomeIcon
                                                        className={theme.notConfirmedIcon}
                                                        icon={faCalendarCheck}
                                                    />
                                                </Tooltip>
                                            ))}
                                    </div>
                                ))}
                            </div>
                            <div className={theme.additionalStatus}>
                                {checkIfAdminAccept(row) && (
                                    <span>
                                        <Tooltip
                                            dynamicWidth={true}
                                            content={
                                                <div>
                                                    <Trans>This meeting has been confirmed by an admin.</Trans>
                                                </div>
                                            }>
                                            <FontAwesomeIcon className={theme.confirmedIcon} icon={faCalendarCheck} />
                                        </Tooltip>
                                    </span>
                                )}
                                {checkIfConnectedMeetings(row) && (
                                    <span>
                                        <Tooltip
                                            dynamicWidth={true}
                                            content={
                                                <div>
                                                    <Trans>This unit has a connected meeting without a date set.</Trans>
                                                </div>
                                            }>
                                            <FontAwesomeIcon className={theme.notConfirmedIcon} icon={faLink} />
                                        </Tooltip>
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                />
                <Table.Column
                    sortable
                    field="sentDate"
                    header={t`Sent date`}
                    renderer={({ row }) => <DateDisplay value={row.sentDate} monthTextForm={true} />}
                />
                <Table.Column
                    sortable
                    hidden={!activeMeetingType.needsConfirmation}
                    field="confirmationDate"
                    header={t`Confirmed Date`}
                    renderer={({ row }) => (
                        <div className={theme.centered}>
                            {row.buyerInfo.users.length !== 0 &&
                                (row.confirmationDate ? (
                                    <span>
                                        <DateDisplay value={row.confirmationDate} monthTextForm={true} />
                                    </span>
                                ) : row.reminderSentDate ? (
                                    <Tooltip
                                        content={
                                            <div>
                                                <div>
                                                    <Trans>Reminder sent</Trans>
                                                </div>
                                                <div>
                                                    <DateDisplay
                                                        value={row.reminderSentDate}
                                                        monthTextForm={true}
                                                    />
                                                </div>
                                            </div>
                                        }>
                                        <Badge variant="primary" appearance="subtle" size="sm">
                                            <Trans>Reminder sent</Trans>
                                        </Badge>
                                    </Tooltip>
                                ) : row.sentDate ? (
                                    <Badge variant="danger" appearance="subtle" size="sm">
                                        <Trans>Not confirmed</Trans>
                                    </Badge>
                                ) : null)}
                        </div>
                    )}
                />
                <Table.Column
                    sortable
                    field="meetingDate"
                    header={t`Meeting date`}
                    renderer={({ row }) => (
                        <span
                            className={classnames(theme.nameInput, theme.link)}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditRow(row);
                            }}>
                            {!row.meetingDate ? (
                                <FontAwesomeIcon icon={faPen} />
                            ) : (
                                <DateDisplay value={row.meetingDate} monthTextForm={true} />
                            )}
                        </span>
                    )}
                />
                <Table.Column
                    header={t`Meeting time`}
                    renderer={({ row }) => (
                        <span
                            className={classnames(theme.nameInput, theme.link)}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditRow(row);
                            }}>
                            {row.timeSlotStart &&
                                row.timeSlotEnd &&
                                numberToClock(row.timeSlotStart) + ' - ' + numberToClock(row.timeSlotEnd)}
                        </span>
                    )}
                />
            </Table>
        </div>
    );
};

export default MeetingApartments;
